import { useMemo } from 'react'
import dayjs from 'dayjs'

import UserOutlinedIcon from '@ant-design/icons/UserOutlined'

import usePatient from '@shared/hooks/src/usePatient'
import { addressToString } from '@shared/utils'

import { BreakGlassPaper } from '@components/BreakGlass'
import Property, { CBOProperty, PatientProperty, ProviderProperty } from '@components/Details/Property'
import Section from '@components/Details/Section'

export default function PatientSection({ patientId, mini = false }) {
  const { data: user, isPending } = usePatient(patientId, {
    enabled: Boolean(patientId),
  })

  const dob = useMemo(() => {
    if (!user?.dob) return '—'
    const age = dayjs().diff(dayjs(user.dob), 'years')
    return `${dayjs(user.dob).format('L')} ${age}yo`
  }, [user])

  const address = useMemo(() => {
    if (!user?.homeAddress) return '—'
    return addressToString(user.homeAddress)
  }, [user])

  if (isPending) return <Loading mini={mini} />

  return (
    <BreakGlassPaper patient={user}>
      <Section paper mini={mini} title="Patient" icon={mini ? null : <UserOutlinedIcon />}>
        <PatientProperty label="Name" user={user} />
        <Property label="DOB">{dob}</Property>
        <Property label="Address" sx={{ value: { whiteSpace: 'pre-wrap' } }}>
          {address}
        </Property>
        <CBOProperty label="CBO" data={user.cbo} />
        <ProviderProperty label="Provider" user={user.provider} />
      </Section>
    </BreakGlassPaper>
  )
}

const Loading = ({ mini = false }) => (
  <Section paper mini={mini} title="Patient" icon={mini ? null : <UserOutlinedIcon />}>
    <PatientProperty.Loading label="Name" />
    <Property.Loading label="DOB" />
    <CBOProperty.Loading label="CBO" />
    <ProviderProperty.Loading label="Provider" />
  </Section>
)

PatientSection.Loading = Loading
